import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, isAdmin } from "../lib/api";
import { CheckCircle2, Circle, Users, Lock, Printer, ChevronDown, ChevronUp } from "lucide-react";

export default function Fixtures() {
  const admin = isAdmin();
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [matchdays, setMatchdays] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedMatchdays, setExpandedMatchdays] = useState({});

  useEffect(() => {
    (async () => {
      const [ss, ps] = await Promise.all([api.listSeasons(), api.listPlayers()]);
      setSeasons(ss); setPlayers(ps);
      const active = ss.find((s) => s.active) || ss[0];
      if (active) setSeasonId(active.id);
    })();
  }, []);

  useEffect(() => {
    if (!seasonId) return;
    Promise.all([api.listMatchdays(seasonId), api.listMatches({ season_id: seasonId })])
      .then(([mds, ms]) => { setMatchdays(mds); setMatches(ms); });
  }, [seasonId]);

  const pmap = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const season = seasons.find((s) => s.id === seasonId);
  const memberIds = new Set(season?.member_ids || season?.player_ids || []);

  const matchesByMatchday = useMemo(() => {
    const g = {};
    for (const m of matches) {
      if (!m.matchday_id) continue;
      if (statusFilter === "open" && m.played) continue;
      if (statusFilter === "played" && !m.played) continue;
      (g[m.matchday_id] = g[m.matchday_id] || []).push(m);
    }
    return g;
  }, [matches, statusFilter]);

  const toggleMatchday = (matchdayId) => {
    setExpandedMatchdays((previous) => ({ ...previous, [matchdayId]: !previous[matchdayId] }));
  };

  return (
    <div className="space-y-6" data-testid="fixtures-page">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>Spielplan</div>
          <h1 className="font-display text-5xl">Spieltage</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="fixtures-season-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active ? " (aktiv)" : ""}</option>)}
          </select>
          <select className="dc-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="fixtures-status-filter">
            <option value="all">Alle</option>
            <option value="open">Offen</option>
            <option value="played">Gespielt</option>
          </select>
        </div>
      </div>

      {matchdays.length === 0 && (
        <div className="dc-border p-10 text-center" style={{ color: "var(--dc-muted)" }}>
          Keine Spieltage. Erstelle eine Saison im Admin-Bereich.
        </div>
      )}

      {matchdays.map((md) => {
        const dayMatches = matchesByMatchday[md.id] || [];
        const expanded = !!expandedMatchdays[md.id];
        return (
          <div key={md.id} className="dc-border p-6 space-y-3" data-testid={`matchday-block-${md.number}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display text-2xl flex items-center gap-3">
                  Spieltag {md.number}
                  {md.date && <span className="text-sm font-normal" style={{ color: "var(--dc-muted)" }}>· {md.date}</span>}
                  {md.generated && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ background: "var(--dc-secondary)", color: "white" }}>Spielplan erstellt</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: "var(--dc-muted)" }}>
                  <Users size={12} />
                  {md.attendee_ids.length > 0 ? `${md.attendee_ids.length} Anwesende` : "Anwesenheit noch offen"}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {dayMatches.length > 0 && (
                  <Link to={`/spieltag-bericht/${md.id}`} className="dc-btn dc-btn-ghost text-xs" data-testid={`matchday-report-${md.id}`}>
                    <Printer size={13} /> Spielbericht / PDF
                  </Link>
                )}
                <button className="dc-btn dc-btn-ghost text-xs" onClick={() => toggleMatchday(md.id)} data-testid={`matchday-toggle-${md.id}`}>
                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {expanded ? "Details schließen" : "Details anzeigen"}
                </button>
              </div>
            </div>

            {expanded && md.attendee_ids.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {md.attendee_ids.map((pid) => {
                  const isGuest = !memberIds.has(pid);
                  return (
                    <span key={pid} className={`text-xs px-2 py-1 dc-border ${isGuest ? "" : ""}`}
                      style={{ background: isGuest ? "rgba(245,209,59,0.12)" : "transparent",
                               color: isGuest ? "var(--dc-chalk)" : "var(--dc-text)",
                               borderColor: isGuest ? "var(--dc-chalk)" : "var(--dc-border)" }}
                      data-testid={`matchday-${md.number}-attendee-${pid}`}>
                      {pmap[pid]?.name || "?"}{isGuest && " · Gast"}
                    </span>
                  );
                })}
              </div>
            )}

            {expanded && !md.generated && (
              <div className="text-sm" style={{ color: "var(--dc-muted)" }}>
                <Lock size={12} className="inline mr-1" />
                Spielplan noch nicht generiert. Admin muss Anwesenheit festlegen und generieren.
              </div>
            )}

            {expanded && dayMatches.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t dc-border">
                {dayMatches.map((m) => {
                  const p1 = pmap[m.player1_id]; const p2 = pmap[m.player2_id];
                  return (
                    <div key={m.id} className="dc-border p-3 flex items-center justify-between hover:bg-[color:var(--dc-surface-hover)]" data-testid={`fixture-${m.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={m.winner_id === m.player1_id ? "font-bold text-white" : ""}>{p1?.name || "?"}</span>
                          <span className="font-mono-data text-xs" style={{ color: "var(--dc-muted)" }}>
                            {m.played ? `${m.legs_p1} : ${m.legs_p2}` : "vs"}
                          </span>
                          <span className={m.winner_id === m.player2_id ? "font-bold text-white" : ""}>{p2?.name || "?"}</span>
                          {!m.is_ranked && <span className="text-[10px] uppercase tracking-widest ml-1" style={{ color: "var(--dc-chalk)" }}>Gast</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.played ? (
                          <>
                            <span className="flex items-center gap-1 text-xs" style={{ color: "var(--dc-secondary)" }}>
                              <CheckCircle2 size={14} />
                            </span>
                            {admin && <Link to={`/erfassen?match=${m.id}`} className="dc-btn dc-btn-ghost text-xs" data-testid={`fixture-edit-${m.id}`}>Bearbeiten</Link>}
                            <Link to={`/bericht/${m.id}`} className="dc-btn dc-btn-ghost text-xs" data-testid={`fixture-report-${m.id}`}>Bericht</Link>
                          </>
                        ) : admin ? (
                          <Link to={`/erfassen?match=${m.id}`} className="dc-btn dc-btn-ghost text-xs" data-testid={`fixture-enter-${m.id}`}>
                            <Circle size={10} /> Eintragen
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
